import { useMemo, useState, useEffect, useRef } from 'react';
import Editor, { type OnMount, type BeforeMount } from '@monaco-editor/react';
import type * as Monaco from 'monaco-editor';

export interface CodeEditorProps {
  codeFromApi: string;
  language: string;
  isEditable?: boolean;
  executable?: number[];
  nonExecutable?: number[];
  covered?: number[];
  uncovered?: number[];
  modified?: number[];
  additions?: number[];
  deletions?: number[];
  instanceId?: string;
  /** Editor viewport height. Use `100%` to fill a sized parent. */
  height?: string;
  /** Nested AST ranges drawn as the structure rail (inventory). */
  structure?: StructureSpan[];
  /** Inventory layout: rail + footer on every file, including files with no spans. */
  structureMode?: boolean;
}

export type StructureSpan = {
  type: 'class' | 'method' | 'function' | 'branch' | 'loop';
  startLine: number;
  endLine: number;
  depth: number;
};

const STRUCTURE_COLORS: Record<StructureSpan['type'], string> = {
  class: '#7c3aed',
  method: '#2563eb',
  function: '#0d9488',
  branch: '#ea580c',
  loop: '#db2777',
};

interface VisibilityState {
  additions: boolean;
  deletions: boolean;
  modified: boolean;
  executable: boolean;
  nonExecutable: boolean;
  covered: boolean;
  uncovered: boolean;
}

interface LegendItem {
  key: keyof VisibilityState;
  gutter: string;
  label: string;
  data: number[];
}

/** SonarQube-style line markers on a black editor background. */
const EDITOR_STYLES = `
.git-line-added   { background-color: rgba(63, 185, 80, 0.18) !important; }
.git-line-added-gutter { border-left: 4px solid #3fb950; }
.git-line-removed { background-color: rgba(248, 81, 73, 0.22) !important; }
.git-line-removed-gutter { border-left: 4px solid #f85149; }
.line-modifed     { background-color: rgba(210, 153, 34, 0.20) !important; }
.gutter-modified  { border-left: 4px solid #d29922; }
.line-executable  { background-color: rgba(63, 185, 80, 0.22) !important; }
.gutter-executable{ border-left: 4px solid #3fb950; }
.line-non-executable  { background-color: rgba(248, 81, 73, 0.22) !important; }
.gutter-non-executable{ border-left: 4px solid #f85149; }
.line-covered     { background-color: rgba(63, 185, 80, 0.22) !important; }
.gutter-covered   { border-left: 4px solid #3fb950; }
.line-uncovered   { background-color: rgba(248, 81, 73, 0.22) !important; }
.gutter-uncovered { border-left: 4px solid #f85149; }
`;

const GITHUB_THEME = 'qtrace-github-dark';
const GITHUB_THEME_DEFINED = { current: false };

const LANGUAGE_ALIASES: Record<string, string> = {
  typescript: 'typescript',
  ts: 'typescript',
  tsx: 'typescript',
  javascript: 'javascript',
  js: 'javascript',
  jsx: 'javascript',
  csharp: 'csharp',
  cs: 'csharp',
  'c#': 'csharp',
  python: 'python',
  py: 'python',
  java: 'java',
  json: 'json',
  html: 'html',
  css: 'css',
  scss: 'scss',
  less: 'less',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  markdown: 'markdown',
  md: 'markdown',
  sql: 'sql',
  shell: 'shell',
  bash: 'shell',
  sh: 'shell',
  powershell: 'powershell',
  go: 'go',
  rust: 'rust',
  ruby: 'ruby',
  php: 'php',
  kotlin: 'kotlin',
  swift: 'swift',
  plaintext: 'plaintext',
};

const EXTENSION_TO_LANGUAGE: Record<string, string> = {
  ts: 'typescript',
  tsx: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  mjs: 'javascript',
  cjs: 'javascript',
  cs: 'csharp',
  py: 'python',
  java: 'java',
  json: 'json',
  html: 'html',
  htm: 'html',
  css: 'css',
  scss: 'scss',
  less: 'less',
  xml: 'xml',
  yml: 'yaml',
  yaml: 'yaml',
  md: 'markdown',
  sql: 'sql',
  sh: 'shell',
  bash: 'shell',
  ps1: 'powershell',
  go: 'go',
  rs: 'rust',
  rb: 'ruby',
  php: 'php',
  kt: 'kotlin',
  swift: 'swift',
};

const LANGUAGE_TO_EXTENSION: Record<string, string> = {
  typescript: 'ts',
  javascript: 'js',
  csharp: 'cs',
  python: 'py',
  java: 'java',
  json: 'json',
  html: 'html',
  css: 'css',
  scss: 'scss',
  less: 'less',
  xml: 'xml',
  yaml: 'yml',
  markdown: 'md',
  sql: 'sql',
  shell: 'sh',
  powershell: 'ps1',
  go: 'go',
  rust: 'rs',
  ruby: 'rb',
  php: 'php',
  kotlin: 'kt',
  swift: 'swift',
  plaintext: 'txt',
};

/** Map API labels / file paths to a Monaco language id. */
function resolveMonacoLanguage(input: string | undefined): string {
  const raw = (input || '').trim();
  if (!raw) return 'plaintext';

  const lower = raw.toLowerCase();
  if (LANGUAGE_ALIASES[lower]) return LANGUAGE_ALIASES[lower];

  // File path / URI → extension
  const base = lower.split(/[\\/]/).pop() || lower;
  const withoutQuery = base.split(/[?#]/)[0];
  const ext = withoutQuery.includes('.')
    ? withoutQuery.split('.').pop() || ''
    : '';
  if (ext && EXTENSION_TO_LANGUAGE[ext]) return EXTENSION_TO_LANGUAGE[ext];

  // "TypeScript", "JavaScript", "CSharp", etc.
  const compact = lower.replace(/[\s_+-]+/g, '');
  if (LANGUAGE_ALIASES[compact]) return LANGUAGE_ALIASES[compact];

  return 'plaintext';
}

function modelPathFor(instanceId: string, monacoLang: string): string {
  const safeId = (instanceId || 'file')
    .replace(/[^a-zA-Z0-9._/-]+/g, '_')
    .replace(/^\/+/, '');
  const ext = LANGUAGE_TO_EXTENSION[monacoLang] || 'txt';
  // Keep a real extension so Monaco can tokenize even if language prop races.
  if (safeId.toLowerCase().endsWith(`.${ext}`)) {
    return `inmemory://model/${safeId}`;
  }
  return `inmemory://model/${safeId}.${ext}`;
}

export default function CodeEditor({
  codeFromApi,
  language,
  isEditable = false,
  executable = [],
  nonExecutable = [],
  covered = [],
  uncovered = [],
  modified = [],
  additions = [],
  deletions = [],
  instanceId = 'default',
  height = '62vh',
  structure = [],
  structureMode = false,
}: CodeEditorProps) {
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<typeof Monaco | null>(null);
  const decorationIdsRef = useRef<string[]>([]);
  const [scrollTop, setScrollTop] = useState(0);
  const [editorReady, setEditorReady] = useState(false);
  const showStructure = structureMode || structure.length > 0;

  const monacoLanguage = useMemo(
    () => resolveMonacoLanguage(language),
    [language],
  );
  const modelPath = useMemo(
    () => modelPathFor(instanceId, monacoLanguage),
    [instanceId, monacoLanguage],
  );

  const [visible, setVisible] = useState<VisibilityState>({
    additions: true,
    deletions: true,
    modified: true,
    executable: true,
    nonExecutable: true,
    covered: true,
    uncovered: true,
  });

  const legendItems: LegendItem[] = (
    [
      {
        key: 'additions' as const,
        gutter: '#3fb950',
        label: 'Added',
        data: additions,
      },
      {
        key: 'deletions' as const,
        gutter: '#f85149',
        label: 'Removed',
        data: deletions,
      },
      {
        key: 'modified' as const,
        gutter: '#d29922',
        label: 'Modified',
        data: modified,
      },
      {
        key: 'executable' as const,
        gutter: '#3fb950',
        label: 'Executable',
        data: executable,
      },
      {
        key: 'nonExecutable' as const,
        gutter: '#f85149',
        label: 'Non-executable',
        data: nonExecutable,
      },
      {
        key: 'covered' as const,
        gutter: '#3fb950',
        label: 'Covered',
        data: covered,
      },
      {
        key: 'uncovered' as const,
        gutter: '#f85149',
        label: 'Uncovered',
        data: uncovered,
      },
    ] as LegendItem[]
  ).filter(
    item =>
      item.data.length > 0 &&
      !(showStructure && (item.key === 'executable' || item.key === 'nonExecutable')),
  );

  const applyHighlights = (
    editor: Monaco.editor.IStandaloneCodeEditor,
    monaco: typeof Monaco,
  ) => {
    const makeDecoration = (
      lines: number[],
      className: string,
      marginClassName: string,
      hoverMessage?: string,
    ): Monaco.editor.IModelDeltaDecoration[] =>
      lines.map(line => ({
        range: new monaco.Range(line, 1, line, 1),
        options: {
          isWholeLine: true,
          className,
          marginClassName,
          ...(hoverMessage ? { hoverMessage: { value: hoverMessage } } : {}),
        },
      }));

    const newDecorations: Monaco.editor.IModelDeltaDecoration[] = [
      ...makeDecoration(
        visible.additions ? additions : [],
        'git-line-added',
        'git-line-added-gutter',
      ),
      ...makeDecoration(
        visible.deletions ? deletions : [],
        'git-line-removed',
        'git-line-removed-gutter',
      ),
      ...makeDecoration(
        visible.modified ? modified : [],
        'line-modifed',
        'gutter-modified',
        '**Modified**',
      ),
      ...makeDecoration(
        visible.executable ? executable : [],
        'line-executable',
        'gutter-executable',
        '**Executable**',
      ),
      ...makeDecoration(
        visible.nonExecutable ? nonExecutable : [],
        'line-non-executable',
        'gutter-non-executable',
        '**Non-executable**',
      ),
      ...makeDecoration(
        visible.covered ? covered : [],
        'line-covered',
        'gutter-covered',
        '**Covered**',
      ),
      ...makeDecoration(
        visible.uncovered ? uncovered : [],
        'line-uncovered',
        'gutter-uncovered',
        '**Executable but uncovered**',
      ),
    ];

    decorationIdsRef.current = editor.deltaDecorations(
      decorationIdsRef.current,
      newDecorations,
    );
  };

  const handleBeforeMount: BeforeMount = monaco => {
    if (GITHUB_THEME_DEFINED.current) return;
    // GitHub Dark (Primer) syntax colors on a black canvas.
    monaco.editor.defineTheme(GITHUB_THEME, {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: '', foreground: 'E6EDF3' },
        { token: 'comment', foreground: '8B949E', fontStyle: 'italic' },
        { token: 'comment.doc', foreground: '8B949E', fontStyle: 'italic' },
        { token: 'string', foreground: 'A5D6FF' },
        { token: 'string.escape', foreground: '79C0FF' },
        { token: 'number', foreground: '79C0FF' },
        { token: 'regexp', foreground: '7EE787' },
        { token: 'keyword', foreground: 'FF7B72' },
        { token: 'keyword.control', foreground: 'FF7B72' },
        { token: 'keyword.operator', foreground: 'FF7B72' },
        { token: 'storage', foreground: 'FF7B72' },
        { token: 'storage.type', foreground: 'FF7B72' },
        { token: 'operator', foreground: 'FF7B72' },
        { token: 'delimiter', foreground: 'E6EDF3' },
        { token: 'delimiter.bracket', foreground: 'E6EDF3' },
        { token: 'type', foreground: 'FFA657' },
        { token: 'type.identifier', foreground: 'FFA657' },
        { token: 'class', foreground: 'FFA657' },
        { token: 'class.name', foreground: 'FFA657' },
        { token: 'interface', foreground: 'FFA657' },
        { token: 'namespace', foreground: 'FFA657' },
        { token: 'struct', foreground: 'FFA657' },
        { token: 'enum', foreground: 'FFA657' },
        { token: 'typeParameter', foreground: 'FFA657' },
        { token: 'function', foreground: 'D2A8FF' },
        { token: 'function.name', foreground: 'D2A8FF' },
        { token: 'method', foreground: 'D2A8FF' },
        { token: 'method.name', foreground: 'D2A8FF' },
        { token: 'member', foreground: 'D2A8FF' },
        { token: 'variable', foreground: 'FFA657' },
        { token: 'variable.name', foreground: 'FFA657' },
        { token: 'variable.predefined', foreground: '79C0FF' },
        { token: 'parameter', foreground: 'FFA657' },
        { token: 'property', foreground: '79C0FF' },
        { token: 'property.name', foreground: '79C0FF' },
        { token: 'constant', foreground: '79C0FF' },
        { token: 'annotation', foreground: 'D2A8FF' },
        { token: 'decorator', foreground: 'D2A8FF' },
        { token: 'tag', foreground: '7EE787' },
        { token: 'tag.id', foreground: '79C0FF' },
        { token: 'tag.class', foreground: '79C0FF' },
        { token: 'attribute.name', foreground: '79C0FF' },
        { token: 'attribute.value', foreground: 'A5D6FF' },
        { token: 'attribute.value.html', foreground: 'A5D6FF' },
        { token: 'meta.tag', foreground: 'E6EDF3' },
        { token: 'metatag', foreground: '7EE787' },
        { token: 'metatag.content', foreground: '79C0FF' },
        { token: 'metatag.html', foreground: '7EE787' },
        { token: 'metatag.xml', foreground: '7EE787' },
        { token: 'key', foreground: '79C0FF' },
        { token: 'string.key', foreground: '79C0FF' },
        { token: 'string.value', foreground: 'A5D6FF' },
        { token: 'identifier', foreground: 'E6EDF3' },
        { token: 'invalid', foreground: 'FFA198' },
      ],
      colors: {
        'editor.background': '#000000',
        'editor.foreground': '#E6EDF3',
        'editorGutter.background': '#000000',
        'editorLineNumber.foreground': '#6E7681',
        'editorLineNumber.activeForeground': '#E6EDF3',
        'editor.lineHighlightBackground': '#161B22',
        'editor.selectionBackground': '#264F78',
        'editor.inactiveSelectionBackground': '#264F7880',
        'editorCursor.foreground': '#E6EDF3',
        'editorWhitespace.foreground': '#484F58',
        'editorIndentGuide.background': '#21262D',
        'editorIndentGuide.activeBackground': '#6E7681',
        'editorBracketMatch.background': '#3FB95040',
        'editorBracketMatch.border': '#3FB950',
      },
    });
    GITHUB_THEME_DEFINED.current = true;
  };

  const handleEditorDidMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    const model = editor.getModel();
    if (model) {
      const current = model.getLanguageId();
      if (current !== monacoLanguage) {
        monaco.editor.setModelLanguage(model, monacoLanguage);
      }
    }

    applyHighlights(editor, monaco);
    setEditorReady(true);
    if (showStructure) setScrollTop(editor.getScrollTop());
  };

  useEffect(() => {
    if (editorRef.current && monacoRef.current) {
      const model = editorRef.current.getModel();
      if (model && model.getLanguageId() !== monacoLanguage) {
        monacoRef.current.editor.setModelLanguage(model, monacoLanguage);
      }
      applyHighlights(editorRef.current, monacoRef.current);
    }
  }, [
    additions,
    deletions,
    executable,
    nonExecutable,
    covered,
    uncovered,
    modified,
    visible,
    monacoLanguage,
    structure,
  ]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !showStructure) return;
    setScrollTop(editor.getScrollTop());
    const sub = editor.onDidScrollChange(e => setScrollTop(e.scrollTop));
    return () => sub.dispose();
  }, [showStructure, instanceId, editorReady]);

  const fillParent = height === '100%';
  const lineHeight = 22;
  const padTop = 8;
  const maxDepth = showStructure
    ? structure.reduce((max, span) => Math.max(max, span.depth), 0)
    : 0;
  const railWidth = showStructure ? (maxDepth + 1) * 7 + 8 : 0;

  return (
    <div
      style={{
        borderRadius: '8px',
        overflow: 'hidden',
        border: '1px solid #30363d',
        background: '#000000',
        ...(fillParent
          ? {
              display: 'flex',
              height: '100%',
              minHeight: 0,
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : {}),
      }}>
      <style>{EDITOR_STYLES}</style>
      {legendItems.length > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexWrap: 'wrap',
            flexShrink: 0,
            padding: '0.5rem 0.75rem',
            background: '#0d1117',
            borderBottom: '1px solid #30363d',
            fontSize: '0.8125rem',
            color: '#c9d1d9',
          }}>
          <span style={{ fontWeight: 600, color: '#e6edf3' }}>Show:</span>
          {legendItems.map(({ key, gutter, label }) => (
            <label
              key={key}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
                cursor: 'pointer',
                userSelect: 'none',
              }}>
              <input
                type="checkbox"
                checked={visible[key]}
                onChange={() =>
                  setVisible(prev => ({ ...prev, [key]: !prev[key] }))
                }
                style={{ cursor: 'pointer' }}
              />
              <span
                style={{
                  display: 'inline-block',
                  width: '4px',
                  height: '14px',
                  borderRadius: '2px',
                  background: gutter,
                  flexShrink: 0,
                }}
              />
              {label}
            </label>
          ))}
        </div>
      )}
      <div
        style={{
          display: 'flex',
          minHeight: 0,
          ...(fillParent
            ? { flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }
            : {}),
        }}>
        {showStructure ? (
          <div
            aria-hidden="true"
            style={{
              width: railWidth,
              flexShrink: 0,
              alignSelf: 'stretch',
              height: '100%',
              overflow: 'hidden',
              position: 'relative',
              background: '#000000',
              borderRight: '1px solid #21262d',
            }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                transform: `translateY(${-scrollTop}px)`,
              }}>
              {structure.map((span, index) => (
                <div
                  key={`${span.type}-${span.startLine}-${span.endLine}-${index}`}
                  title={span.type}
                  style={{
                    position: 'absolute',
                    left: 4 + span.depth * 7,
                    top: padTop + (span.startLine - 1) * lineHeight,
                    height: Math.max(
                      lineHeight,
                      (span.endLine - span.startLine + 1) * lineHeight,
                    ),
                    width: 3,
                    borderRadius: 2,
                    background: STRUCTURE_COLORS[span.type],
                  }}
                />
              ))}
            </div>
          </div>
        ) : null}
        <div
          style={
            fillParent
              ? {
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  overflow: 'hidden',
                }
              : { flex: 1, minWidth: 0 }
          }>
          <div
            style={
              fillParent
                ? { position: 'absolute', inset: 0 }
                : undefined
            }>
            <Editor
              height={fillParent ? '100%' : height}
              theme={GITHUB_THEME}
              language={monacoLanguage}
              path={modelPath}
              value={codeFromApi}
              beforeMount={handleBeforeMount}
              onMount={handleEditorDidMount}
              options={{
                readOnly: !isEditable,
                domReadOnly: !isEditable,
                minimap: { enabled: false },
                fontSize: 12,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                fontLigatures: false,
                lineHeight,
                wordWrap: showStructure ? 'off' : 'on',
                renderLineHighlight: 'all',
                scrollBeyondLastLine: false,
                padding: { top: padTop, bottom: 8 },
                bracketPairColorization: { enabled: true },
                automaticLayout: true,
              }}
            />
          </div>
        </div>
      </div>
      {showStructure ? (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            flexShrink: 0,
            padding: '0.55rem 0.85rem',
            background: 'var(--inv-surface, #faf8f5)',
            borderTop: '1px solid var(--inv-border, #e8e0d4)',
            fontSize: '0.75rem',
            color: 'var(--inv-text-secondary, #6b6258)',
          }}>
          <span style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.7rem' }}>
            {(
              [
                ['executable', 'Executable', '#3fb950'] as const,
                ['nonExecutable', 'Non-executable', '#f85149'] as const,
              ]
            ).map(([key, label, color]) => (
              <label
                key={key}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}>
                <input
                  type="checkbox"
                  checked={visible[key]}
                  onChange={() =>
                    setVisible(prev => ({ ...prev, [key]: !prev[key] }))
                  }
                  style={{ cursor: 'pointer' }}
                />
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: color,
                  }}
                />
                {label}
              </label>
            ))}
            <span style={{ opacity: 0.4 }}>|</span>
            {(
              [
                ['class', 'Class'],
                ['method', 'Method'],
                ['function', 'Function'],
                ['branch', 'Branch'],
                ['loop', 'Loop'],
              ] as const
            ).map(([type, label]) => (
              <span
                key={type}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                <span
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: 2,
                    background: STRUCTURE_COLORS[type],
                  }}
                />
                {label}
              </span>
            ))}
          </span>
          <span>Line color = executable · rail = structure</span>
        </div>
      ) : null}
    </div>
  );
}
